import {openResourceModal} from '../../core/modalCommand';
import {
	SWITCH_CONFIGURE_MODAL_EVENT,
	SWITCH_CONFIGURE_PORT_MODAL_EVENT,
	SWITCH_CONTINUOUS_POE_MODAL_EVENT,
	SWITCH_CREATE_MODAL_EVENT,
	SWITCH_CREATE_VLAN_MODAL_EVENT,
	SWITCH_DELETE_VLAN_MODAL_EVENT,
	SWITCH_DELETE_MODAL_EVENT,
	SWITCH_FACTORY_RESET_MODAL_EVENT,
	SWITCH_MOVE_MODAL_EVENT,
	SWITCH_PARTITION_MODAL_EVENT,
	SWITCH_REBOOT_MODAL_EVENT,
} from './events';

/**
 * Open Add Switch.
 * @param {object} command modal command
 * @param {object} envelope request correlation
 * @return {Promise<void>} modal acknowledgement
 */
export function openSwitchCreateModal(command, envelope) {
	return openResourceModal(SWITCH_CREATE_MODAL_EVENT, command, envelope);
}

/**
 * Open Configure Switch.
 *
 * Canvas refuses this for a switch that is offline, and says so in a toast
 * rather than opening anything. The listener reports that back as a failure
 * so the reply can say why instead of claiming a form is open.
 *
 * @param {object} command modal command
 * @param {object} envelope request correlation
 * @return {Promise<void>} modal acknowledgement
 */
export function openSwitchConfigureModal(command, envelope) {
	return openResourceModal(SWITCH_CONFIGURE_MODAL_EVENT, command, envelope);
}

/**
 * Open Move Switch.
 * @param {object} command modal command
 * @param {object} envelope request correlation
 * @return {Promise<void>} modal acknowledgement
 */
export function openSwitchMoveModal(command, envelope) {
	return openResourceModal(SWITCH_MOVE_MODAL_EVENT, command, envelope);
}

/**
 * Open the Reboot confirmation.
 *
 * The confirmation, not the reboot. Nothing restarts until the person
 * presses the button inside it.
 *
 * @param {object} command modal command
 * @param {object} envelope request correlation
 * @return {Promise<void>} modal acknowledgement
 */
export function openSwitchRebootModal(command, envelope) {
	return openResourceModal(SWITCH_REBOOT_MODAL_EVENT, command, envelope);
}

/**
 * Open the Delete confirmation.
 *
 * The confirmation, not the deletion.
 *
 * @param {object} command modal command
 * @param {object} envelope request correlation
 * @return {Promise<void>} modal acknowledgement
 */
export function openSwitchDeleteModal(command, envelope) {
	return openResourceModal(SWITCH_DELETE_MODAL_EVENT, command, envelope);
}

/**
 * Open the Factory Reset confirmation.
 *
 * The confirmation, not the reset. This is the one that cannot be undone at
 * all, and it is still only ever opened.
 *
 * @param {object} command modal command
 * @param {object} envelope request correlation
 * @return {Promise<void>} modal acknowledgement
 */
export function openSwitchFactoryResetModal(command, envelope) {
	return openResourceModal(
		SWITCH_FACTORY_RESET_MODAL_EVENT, command, envelope,
	);
}

/**
 * Open the Partition form.
 * @param {object} command modal command
 * @param {object} envelope request correlation
 * @return {Promise<void>} modal acknowledgement
 */
export function openSwitchPartitionModal(command, envelope) {
	return openResourceModal(SWITCH_PARTITION_MODAL_EVENT, command, envelope);
}

/**
 * Open the Continuous PoE Power form.
 * @param {object} command modal command
 * @param {object} envelope request correlation
 * @return {Promise<void>} modal acknowledgement
 */
export function openSwitchContinuousPoeModal(command, envelope) {
	return openResourceModal(
		SWITCH_CONTINUOUS_POE_MODAL_EVENT, command, envelope,
	);
}

// The Configure Port form is the one modal whose listener cannot answer
// from what is already rendered: it has to find the port among rows the
// Ports section fetches when it opens, and the dispatcher starts sending
// the moment that section reports itself open. The default budget assumes
// it is waiting on a child component to mount - a render or two - which is
// why it is five seconds. Here it is waiting on a request, so the form
// opened on screen and was still reported as having timed out. Matched to
// the twelve seconds a section itself gets, and still under the command
// dispatcher's own fifteen.
const PORT_FORM_ACKNOWLEDGEMENT_MS = 12000;

/**
 * Open the Configure Port form for one port on a switch.
 *
 * A port is a record inside a switch, the way a rule is inside an ACL, so
 * which port travels beside the switch that owns it.
 *
 * @param {object} command modal command
 * @param {object} envelope request correlation
 * @return {Promise<void>} modal acknowledgement
 */
export function openSwitchConfigurePortModal(command, envelope) {
	return openResourceModal(
		SWITCH_CONFIGURE_PORT_MODAL_EVENT,
		command,
		envelope,
		PORT_FORM_ACKNOWLEDGEMENT_MS,
	);
}

/**
 * Open Add VLAN.
 * @param {object} command modal command
 * @param {object} envelope request correlation
 * @return {Promise<void>} modal acknowledgement
 */
export function openSwitchCreateVlanModal(command, envelope) {
	return openResourceModal(SWITCH_CREATE_VLAN_MODAL_EVENT, command, envelope);
}

/**
 * Open the Delete VLAN confirmation.
 *
 * The confirmation, not the deletion. Which VLAN travels as its id, the way
 * a rule sequence travels beside its ACL.
 *
 * @param {object} command modal command
 * @param {object} envelope request correlation
 * @return {Promise<void>} modal acknowledgement
 */
export function openSwitchDeleteVlanModal(command, envelope) {
	return openResourceModal(SWITCH_DELETE_VLAN_MODAL_EVENT, command, envelope);
}
